// '/pure': Stripe.js is injected only when loadStripe() runs (type 6 facilities). The default
// entry injects it on import, i.e. on every page and for every gateway.
import { loadStripe } from '@stripe/stripe-js/pure'
import { getStripeAppearance, stripeFonts } from './stripeAppearance'
import { stripePaymentToken } from './stripePayload'
import { getConfigDetails } from '../../services/config'
import { PAYMENT_GATEWAY } from '../../constants/globalConstants'

// The customer portal's facility objects carry facility_payment_details directly
export const getPaymentDetails = (facilityData) => facilityData?.facility_payment_details || facilityData?.facility?.facility_payment_details

export const isStripeGateway = (facilityData) => {
    return String(getPaymentDetails(facilityData)?.facility_payment_type_id) === PAYMENT_GATEWAY.STRIPE
}

// loadStripe must run once per key/account; cache the promise
const stripePromises = {}

// Only the facility's own publishable key is used (facility_payment_details.stripe_publishable_key)
export const getStripe = (publishableKey, stripeAccount) => {
    const key = publishableKey || ''
    if (!key) {
        return null
    }
    const cacheKey = `${key}|${stripeAccount || ''}`
    if (!stripePromises[cacheKey]) {
        // developerTools: the floating "stripe" testing-assistant badge (test keys only) is hidden
        // when REACT_APP_STRIPE_HIDE_TEST_BADGE=true
        stripePromises[cacheKey] = loadStripe(key, {
            ...(stripeAccount ? { stripeAccount } : {}),
            ...(getConfigDetails()?.stripeHideTestBadge ? { developerTools: { assistant: { enabled: false } } } : {})
        })
    }
    return stripePromises[cacheKey]
}

// Publishable key / connected account come from facility_payment_details, like
// heartland_public_api_key. Never a secret key.
export const getStripeForFacility = (facilityData) => {
    const details = getPaymentDetails(facilityData)
    return getStripe(details?.stripe_publishable_key, details?.stripe_account_id)
}

export const getStripeCurrency = (facilityData) => {
    return (getPaymentDetails(facilityData)?.stripe_currency || 'usd').toLowerCase()
}

export const toStripeAmount = (amount) => Math.round(Number(amount || 0) * 100)

// Capture method per flow, matching how the other gateways charge today:
// SALE     - permits, permit requests and reservations are captured immediately
// PRE_AUTH - authorize now, capture later (transient check-in / extend)
export const STRIPE_CAPTURE = {
    PRE_AUTH: 'manual',
    SALE: 'automatic'
}

// Deferred-intent Elements options: no client secret needed to render. Must match the
// PaymentIntent the backend creates for that flow (same capture_method and card saving).
// setupFutureUsage: 'off_session' only when the card is kept for later payments (the backend saves it
// with the customer's consent, user_consent); null leaves it out.
export const stripePaymentElementsOptions = (facilityData, amount, captureMethod = STRIPE_CAPTURE.SALE, setupFutureUsage = null) => ({
    mode: 'payment',
    amount: toStripeAmount(amount),
    currency: getStripeCurrency(facilityData),
    captureMethod,
    ...(setupFutureUsage ? { setupFutureUsage } : {}),
    paymentMethodTypes: ['card'],
    appearance: getStripeAppearance(),
    fonts: stripeFonts
})

// Add Card: the backend creates and confirms a SetupIntent with the confirmation token
export const stripeSetupElementsOptions = (facilityData) => ({
    mode: 'setup',
    currency: getStripeCurrency(facilityData),
    setupFutureUsage: 'off_session',
    paymentMethodTypes: ['card'],
    appearance: getStripeAppearance(),
    fonts: stripeFonts
})

// Backend answered: the bank needs 3DS / SCA (errors.detail.requires_action + client_secret)
// sentPayload: the request just sent. If it already carried payment_intent_id / setup_intent_id (the 3DS retry),
// null is returned so the screen shows the response as a normal error (retry only once).
// 3DS is off (REACT_APP_STRIPE_3DS_ENABLED, kept in step with the backend's STRIPE_ENABLE_3DS_CONFIRMATION):
// null as well, the response is shown as a normal payment error and no Stripe popup opens.
export const getStripeRequiredAction = (res, sentPayload) => {
    if (!getConfigDetails()?.stripe3dsEnabled) return null
    if (sentPayload?.payment_intent_id || sentPayload?.setup_intent_id) return null
    const detail = res?.errors?.detail
    return detail?.requires_action && detail?.client_secret ? detail : null
}

// Runs the 3DS challenge; resolves with the paymentToken to resend (pi_... / seti_...).
// Stripe is always loaded with the facility's own key / connected account (facility_payment_details),
// the same ones the card form used, so it works for every facility.
export const completeStripeRequiredAction = async (detail, facilityData) => {
    const stripe = await getStripeForFacility(facilityData)
    if (!stripe) {
        return { error: { message: 'Stripe is not configured' } }
    }
    const result = await stripe.handleNextAction({ clientSecret: detail.client_secret })
    if (result.error) {
        return { error: result.error }
    }
    const intent = result.paymentIntent || result.setupIntent
    return { token: stripePaymentToken(intent?.id) }
}
